import React from 'react';

/**
 * ProgressEvent type matches the agent-progress JSON payload emitted by the Agent.
 * During EXECUTION, additional context fields are included (taskId, role, feature).
 */
type FileTargets = {
  cloud?: string[];
  public?: string[];
  data?: string[];
};

type ProgressEvent = {
  type?: 'progress';
  status?: 'start' | 'done' | 'error';
  state?: 'CHAT' | 'PLAN' | 'TECH_PLAN' | 'EXECUTION' | string;
  tool?: string;
  stage?: string;
  detail?: string;
  // EXECUTION context fields (populated when working on a task queue)
  taskId?: string;
  role?: 'BACKEND_ENGINEER' | 'PRODUCT_ENGINEER' | string;
  feature?: string;
  completedTaskId?: string;
  // File lists sourced from the Tech Plan (phase_start) or tool args (phase_done)
  targets?: FileTargets;
  changed?: FileTargets;
};

/**
 * Returns a user-friendly label for the role.
 */
function getRoleLabel(role?: string): string | null {
  const r = (role || '').toUpperCase();
  if (r === 'BACKEND_ENGINEER') return 'Backend';
  if (r === 'PRODUCT_ENGINEER') return 'Frontend';
  return null;
}

/**
 * State/Role-aware icon component.
 * During EXECUTION, the icon reflects the current role (Backend vs Frontend).
 */
function StateIcon({ state, role }: { state?: string; role?: string }) {
  const s = (state || '').toUpperCase();
  const r = (role || '').toUpperCase();

  const iconClass = 'h-5 w-5 flex-shrink-0';

  // EXECUTION: use role-specific icons
  if (s === 'EXECUTION') {
    // Backend Engineer: Database/server icon
    if (r === 'BACKEND_ENGINEER') {
      return (
        <svg viewBox="0 0 24 24" fill="none" className={`${iconClass} text-[#f59e0b]`} aria-label="Backend">
          <ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M21 5v14c0 1.66-4.03 3-9 3s-9-1.34-9-3V5" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      );
    }

    // Product Engineer: UI/Layout icon
    if (r === 'PRODUCT_ENGINEER') {
      return (
        <svg viewBox="0 0 24 24" fill="none" className={`${iconClass} text-[#8b5cf6]`} aria-label="Frontend">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M3 9h18" stroke="currentColor" strokeWidth="1.5" />
          <path d="M9 9v12" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    }

    // Default EXECUTION icon (workflow/tools)
    return (
      <svg viewBox="0 0 32 32" fill="none" className={`${iconClass} text-[#94a3b8]`} aria-label="Building">
        <path
          d="M25,21c-.7396,0-1.4241,.2155-2.0191,.5669l-2.0314-2.0314-1.4141,1.4141,2.0314,2.0314c-.3514,.595-.5668,1.2795-.5668,2.019,0,2.2056,1.7944,4,4,4,.3557,0,.6943-.0615,1.0228-.1492l-2.4368-2.4368,.0004-.0005c-.3621-.3621-.5864-.8621-.5864-1.4136,0-1.103,.897-2,2-2,.5515,0,1.0515,.2242,1.4136,.5864l.0004-.0005,2.4368,2.4368c.0875-.3284,.1491-.667,.1491-1.0227,0-2.2056-1.7944-4-4-4Z"
          fill="currentColor"
        />
        <path
          d="M20.9495,12.4644l3.7645-3.7645c.3911,.1868,.8237,.3,1.2861,.3,1.6569,0,3-1.3431,3-3s-1.3431-3-3-3-3,1.3431-3,3c0,.4622,.1132,.8948,.2999,1.2859l-3.7645,3.7645,1.4141,1.4141Zm5.0505-7.4644c.5514,0,1,.4486,1,1s-.4486,1-1,1-1-.4486-1-1,.4486-1,1-1Z"
          fill="currentColor"
        />
        <path
          d="M16,12c-2.2092,0-4,1.7908-4,4,0,.7405,.215,1.4254,.5657,2.0201l-5.2795,5.2799c-.3911-.1868-.8238-.3-1.2861-.3-1.6569,0-3,1.3431-3,3s1.3431,3,3,3,3-1.3431,3-3c0-.4622-.1132-.8948-.2999-1.2858l5.2799-5.2799c.5948,.3507,1.2795,.5657,2.02,.5657,2.2091,0,4-1.7909,4-4s-1.7909-4-4-4ZM6,27c-.5514,0-1-.4486-1-1s.4486-1,1-1,1,.4486,1,1-.4486,1-1,1Zm10-9c-1.1028,0-2-.8972-2-2s.8972-2,2-2,2,.8972,2,2-.8972,2-2,2Z"
          fill="currentColor"
        />
        <path
          d="M7,11c.7396,0,1.4241-.2155,2.0191-.5669l2.0311,2.0311,1.4141-1.4141-2.0311-2.0311c.3514-.595,.5668-1.2795,.5668-2.019,0-2.2056-1.7944-4-4-4-.3557,0-.6943,.0615-1.0228,.1492l2.4368,2.4368-.0004,.0005c.3621,.3621,.5864,.8621,.5864,1.4136,0,1.103-.897,2-2,2-.5515,0-1.0515-.2242-1.4136-.5864l-.0004,.0005-2.4368-2.4368c-.0875,.3284-.1491,.667-.1491,1.0227,0,2.2056,1.7944,4,4,4Z"
          fill="currentColor"
        />
      </svg>
    );
  }

  // PLAN: Cursor-style outline/list (bullet + line rows)
  if (s === 'PLAN' || s === 'TECH_PLAN') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={`${iconClass} text-[#94a3b8]`} aria-label="Planning">
        <circle cx="5" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.5" />
        <line x1="10" y1="6" x2="20" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="5" cy="12" r="1.5" stroke="currentColor" strokeWidth="1.5" />
        <line x1="10" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="5" cy="18" r="1.5" stroke="currentColor" strokeWidth="1.5" />
        <line x1="10" y1="18" x2="20" y2="18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }

  // Default: CHAT (chat bubble with tail)
  return (
    <svg viewBox="0 0 24 24" fill="none" className={`${iconClass} text-[#94a3b8]`} aria-label="Chatting">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Flatten a FileTargets object into a single deduplicated path list (public first).
 */
function flattenTargets(t?: FileTargets): string[] {
  if (!t) return [];
  const all = [
    ...(Array.isArray(t.public) ? t.public : []),
    ...(Array.isArray(t.cloud) ? t.cloud : []),
    ...(Array.isArray(t.data) ? t.data : []),
  ].filter(Boolean);
  return Array.from(new Set(all));
}

/**
 * Compact inline file list with truncation.
 */
function FileList({ files }: { files: string[] }) {
  if (!files.length) return null;
  const MAX_SHOWN = 3;
  const shown = files.slice(0, MAX_SHOWN);
  const extra = files.length - shown.length;

  return (
    <div className="flex items-center gap-1 text-[11px] leading-4 text-[#6b7280] font-mono truncate mt-0.5">
      <svg viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3 flex-shrink-0 opacity-60">
        <path d="M1.75 1A1.75 1.75 0 000 2.75v10.5C0 14.216.784 15 1.75 15h12.5A1.75 1.75 0 0016 13.25v-8.5A1.75 1.75 0 0014.25 3H7.5a.25.25 0 01-.2-.1l-.9-1.2c-.33-.44-.85-.7-1.4-.7H1.75z" />
      </svg>
      <span className="truncate">
        {shown.join(', ')}
        {extra > 0 && <span className="text-[#4b5563]"> +{extra} more</span>}
      </span>
    </div>
  );
}

/**
 * AgentProgressTicker - Shows real-time progress during Agent operations.
 *
 * UX goals:
 * - Always show "typing" dots so users see the agent is working.
 * - Display the main stage message prominently.
 * - During EXECUTION, show role badge (Backend/Frontend) and feature name for context.
 * - Show planned file list when available (phase_start targets).
 * - Show secondary detail line when available.
 */
export default function AgentProgressTicker({
  event,
}: {
  event?: ProgressEvent | null;
}) {
  const stage = (event?.stage || '').trim() || 'Working…';
  const detail = (event?.detail || '').trim();
  const feature = (event?.feature || '').trim();
  const role = event?.role;
  const roleLabel = getRoleLabel(role);

  // Determine if we should show execution context (role/feature)
  const isExecution = (event?.state || '').toUpperCase() === 'EXECUTION';
  const showContext = isExecution && (roleLabel || feature);

  // File list: show planned targets from phase_start events
  const targetFiles = React.useMemo(() => flattenTargets(event?.targets), [event?.targets]);

  return (
    <div
      className="mt-4 flex items-center gap-3"
      aria-live="polite"
      aria-label="Progress"
    >
      {/* Left: state/role icon (deterministic, colored by role). */}
      <StateIcon state={event?.state} role={role} />

      {/* Middle: context + messages. */}
      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        {/* Context line: Role badge + Feature name (only during EXECUTION) */}
        {showContext && (
          <div className="flex items-center gap-1.5 text-[11px] leading-4">
            {roleLabel && (
              <span
                className={`px-1.5 py-0.5 rounded font-medium ${
                  (role || '').toUpperCase() === 'BACKEND_ENGINEER'
                    ? 'bg-[#f59e0b]/15 text-[#f59e0b]'
                    : 'bg-[#8b5cf6]/15 text-[#8b5cf6]'
                }`}
              >
                {roleLabel}
              </span>
            )}
            {feature && (
              <span className="text-[#6b7280] truncate">{feature}</span>
            )}
          </div>
        )}

        {/* Main stage message */}
        <div className="text-[14px] leading-5 text-[#e5e7eb] truncate">
          {stage}
        </div>

        {/* Secondary detail line */}
        {detail && (
          <div className="text-[12px] leading-4 text-[#9ca3af] truncate">
            {detail}
          </div>
        )}

        {/* Planned file list (from phase_start targets) */}
        {isExecution && targetFiles.length > 0 && (
          <FileList files={targetFiles} />
        )}
      </div>

      {/* Right: typing dots (blue, WhatsApp-style slower bounce). */}
      <div className="flex items-center gap-1" aria-hidden="true">
        <span
          className="h-2 w-2 rounded-full bg-[#3b82f6]"
          style={{
            animation: 'typing-bounce 1.4s ease-in-out infinite',
            animationDelay: '0ms',
          }}
        />
        <span
          className="h-2 w-2 rounded-full bg-[#3b82f6]"
          style={{
            animation: 'typing-bounce 1.4s ease-in-out infinite',
            animationDelay: '200ms',
          }}
        />
        <span
          className="h-2 w-2 rounded-full bg-[#3b82f6]"
          style={{
            animation: 'typing-bounce 1.4s ease-in-out infinite',
            animationDelay: '400ms',
          }}
        />
      </div>

      {/* Keyframes: slower, smoother bounce like WhatsApp */}
      <style>{`
        @keyframes typing-bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
}
